/**
 * ONE FIGURE, DAY BY DAY (design 18; the owner, 2026-10-05: "maybe like charts and different
 * components / bars"). Drawn here in SVG - no chart library: the one chart Statistics needs is a
 * row of bars, and recharts 3 (≈151 KB min+gzip) would make its surface an unnamed
 * `role="application"` tab stop by default.
 *
 * WHAT IT DRAWS, and why each:
 *   - ONE BAR PER DAY, gold - a count that fills up from zero each day reads as bars (Datawrapper,
 *     Polaris; Jetpack put bars back as its default on 2026-09-30 after trying lines).
 *   - THE SAME DAYS OF THE PERIOD BEFORE, a grey dashed line over the bars, day 1 against day 1 -
 *     only when that read answered (`before`), so no line ever stands for a period nobody read.
 *   - A DOT UNDER EACH DAY AN ARTICLE WAS PUBLISHED, so a rise explains itself (Substack, beehiiv).
 *   - THREE GRIDLINES WITH THEIR NUMBERS, and a few days named under the axis.
 *
 * ★ NOTHING IS ONLY IN A HOVER. The line above the chart names the busiest day; pointing at a bar
 * rewrites it for that day - a mouse extra. Every day's figure, the period before's beside it and
 * the publications are in the table "Show as a table" puts in the chart's place, a real `<table>`
 * with a caption. The SVG is one image with a one-sentence name (the period and its busiest day),
 * and it takes no keyboard focus: there is nothing in it a keyboard needs that the table lacks
 * (Chartability's "No table" and "Content is only visual"; WCAG 1.4.13 is not engaged - nothing
 * pops up).
 *
 * ★ DRAWN AT ITS OWN WIDTH. The SVG's coordinates are the measured pixels of its box (Mantine's
 * `useElementSize`), so the day labels stay 11px on a phone instead of a scaled-down drawing; until
 * the box is measured (and in a test, where nothing is) it draws at 720.
 */
import { useElementSize } from '@mantine/hooks';
import { useState } from 'react';
import type { PointerEvent } from 'react';
import { formatCount, formatShortDay } from '../../../features/cabinet/format';
import { Trans, useTranslation } from '../../../i18n';
import type { ArticlesT } from '../articles-i18n';
import classes from './day-chart.module.css';

export interface ChartDay {
  /** `YYYY-MM-DD`, a UTC day. */
  readonly day: string;
  readonly value: number;
}

export interface DayChartProps {
  /** The figure's name: "Views". */
  readonly title: string;
  /** A figure worded with its unit: "412 views". */
  readonly worded: (value: number) => string;
  /** Every day of the period, oldest first. */
  readonly days: readonly ChartDay[];
  /** The period before, day for day; `undefined` when it did not load (no line is drawn). */
  readonly before?: readonly number[] | undefined;
  /** The days an article was published. */
  readonly published?: ReadonlySet<string> | undefined;
  /** The legend's words for the two periods: "These 30 days", "The 30 days before". */
  readonly these: string;
  readonly beforeWords: string;
  /** The period before inside a sentence ("the 30 days before"); absent, `beforeWords` lower-cased. */
  readonly beforeInSentence?: string | undefined;
  /** What to say when every day is 0: "No views on any of these days." */
  readonly empty: string;
}

const FALLBACK_WIDTH = 720;

export function DayChart({
  title,
  worded,
  days,
  before,
  published,
  these,
  beforeWords,
  beforeInSentence,
  empty,
}: DayChartProps) {
  const { t } = useTranslation('articles');
  const { ref, width } = useElementSize();
  const [pointed, setPointed] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);

  const values = days.map((day) => day.value);
  const comparable = before !== undefined && before.length === days.length ? before : undefined;
  const busiest = values.reduce(
    (best, value, index) => (value > (values[best] ?? 0) ? index : best),
    0,
  );
  const largest = Math.max(0, ...values, ...(comparable ?? []));
  const anyPublished = published !== undefined && days.some((day) => published.has(day.day));

  if (days.length === 0 || largest === 0) {
    return <p className={classes.empty}>{empty}</p>;
  }

  const first = days[0];
  const last = days[days.length - 1];
  const peak = days[busiest];
  const summary =
    first === undefined || last === undefined || peak === undefined
      ? title
      : t('stats.dayChart.summary', {
          title,
          first: formatShortDay(first.day),
          last: formatShortDay(last.day),
          peak: formatShortDay(peak.day),
          value: worded(peak.value),
        });

  return (
    <div className={classes.chart}>
      <p className={classes.readout}>
        {readout(
          days,
          comparable,
          published,
          worded,
          beforeInSentence ?? beforeWords.toLowerCase(),
          pointed ?? busiest,
          pointed !== null,
          t,
        )}
      </p>

      {asTable ? (
        <DayTable
          title={title}
          days={days}
          before={comparable}
          published={published}
          these={these}
          beforeWords={beforeWords}
        />
      ) : (
        <div ref={ref} className={classes.plot}>
          <Bars
            width={width > 0 ? width : FALLBACK_WIDTH}
            days={days}
            before={comparable}
            published={published}
            highlighted={pointed ?? busiest}
            largest={largest}
            summary={summary}
            onPoint={setPointed}
          />
        </div>
      )}

      <div className={classes.foot}>
        <ul className={classes.legend} aria-label={t('stats.dayChart.legendLabel')}>
          <li>
            <span aria-hidden="true" className={classes.keyBar} />
            {these}
          </li>
          {comparable === undefined ? null : (
            <li>
              <span aria-hidden="true" className={classes.keyLine} />
              {beforeWords}
            </li>
          )}
          {anyPublished ? (
            <li>
              <span aria-hidden="true" className={classes.keyDot} />
              {t('stats.dayChart.publishedKey')}
            </li>
          ) : null}
        </ul>
        <button
          type="button"
          className={classes.toggle}
          onClick={() => {
            setAsTable((shown) => !shown);
            setPointed(null);
          }}
        >
          {asTable ? t('stats.dayChart.showChart') : t('stats.dayChart.showTable')}
        </button>
      </div>
    </div>
  );
}

/** The line above the chart: the busiest day, or the day pointed at with what stood beside it. */
function readout(
  days: readonly ChartDay[],
  before: readonly number[] | undefined,
  published: ReadonlySet<string> | undefined,
  worded: (value: number) => string,
  beforeInSentence: string,
  index: number,
  pointed: boolean,
  t: ArticlesT,
) {
  const day = days[index];
  if (day === undefined) {
    return null;
  }
  const earlier = before?.[index];
  const values = { day: formatShortDay(day.day), value: worded(day.value) };
  const strong = { strong: <strong /> };
  return pointed ? (
    <>
      <Trans t={t} i18nKey="stats.dayChart.pointed" values={values} components={strong} />
      {earlier === undefined ? null : (
        <span className={classes.readoutQuiet}>
          {' '}
          {t('stats.dayChart.sameDayBefore', {
            value: formatCount(earlier),
            before: beforeInSentence,
          })}
        </span>
      )}
      {published?.has(day.day) === true ? (
        <span className={classes.readoutQuiet}> · {t('stats.dayChart.youPublished')}</span>
      ) : null}
    </>
  ) : (
    <Trans t={t} i18nKey="stats.dayChart.busiest" values={values} components={strong} />
  );
}

/* ----------------------------------------------------------------------------- the drawing */

interface BarsProps {
  readonly width: number;
  readonly days: readonly ChartDay[];
  readonly before: readonly number[] | undefined;
  readonly published: ReadonlySet<string> | undefined;
  readonly highlighted: number;
  readonly largest: number;
  readonly summary: string;
  readonly onPoint: (index: number | null) => void;
}

const LEFT = 44;
const RIGHT = 6;
const TOP = 10;

function Bars({
  width,
  days,
  before,
  published,
  highlighted,
  largest,
  summary,
  onPoint,
}: BarsProps) {
  const height = width < 480 ? 170 : 210;
  const floor = height - 34;
  const count = days.length;
  const step = (width - LEFT - RIGHT) / count;
  const barWidth = Math.max(1, step * 0.66);
  const top = niceCeiling(largest);
  const y = (value: number): number => floor - (value / top) * (floor - TOP);
  const centre = (index: number): number => LEFT + index * step + step / 2;
  const ticks = Number.isInteger(top / 2) ? [0, top / 2, top] : [0, top];
  const named = namedDays(count, width);

  const point = (event: PointerEvent<SVGSVGElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    const x = box.width > 0 ? ((event.clientX - box.left) * width) / box.width : 0;
    const index = Math.floor((x - LEFT) / step);
    onPoint(index >= 0 && index < count ? index : null);
  };

  return (
    <svg
      className={classes.svg}
      width={width}
      height={height}
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      role="img"
      aria-label={summary}
      onPointerMove={point}
      onPointerLeave={() => {
        onPoint(null);
      }}
    >
      {ticks.map((tick) => (
        <g key={tick}>
          <line
            className={tick === 0 ? classes.axis : classes.grid}
            x1={LEFT}
            x2={width - RIGHT}
            y1={y(tick)}
            y2={y(tick)}
          />
          <text className={classes.tick} x={LEFT - 6} y={y(tick) + 4} textAnchor="end">
            {formatCount(tick)}
          </text>
        </g>
      ))}
      {days.map((day, index) => (
        <rect
          key={day.day}
          className={index === highlighted ? classes.barStrong : classes.bar}
          x={LEFT + index * step + (step - barWidth) / 2}
          y={y(day.value)}
          width={barWidth}
          height={Math.max(0, floor - y(day.value))}
          rx={Math.min(2, barWidth / 3)}
        />
      ))}
      {before === undefined ? null : (
        <polyline
          className={classes.beforeLine}
          points={before
            .map((value, index) => `${String(centre(index))},${String(y(value))}`)
            .join(' ')}
        />
      )}
      {published === undefined
        ? null
        : days.map((day, index) =>
            published.has(day.day) ? (
              <circle
                key={day.day}
                className={classes.dot}
                cx={centre(index)}
                cy={floor + 10}
                r={3.5}
              />
            ) : null,
          )}
      {named.map((index, position) => {
        const day = days[index];
        return day === undefined ? null : (
          <text
            key={day.day}
            className={classes.tick}
            x={centre(index)}
            y={height - 6}
            textAnchor={
              named.length === 1
                ? 'middle'
                : position === 0
                  ? 'start'
                  : position === named.length - 1
                    ? 'end'
                    : 'middle'
            }
          >
            {formatShortDay(day.day)}
          </text>
        );
      })}
    </svg>
  );
}

/** The gridline's top: 1, 2 or 5 times a power of ten, at or above the largest figure. */
export function niceCeiling(value: number): number {
  if (value <= 1) {
    return 1;
  }
  const power = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 5, 10]) {
    if (step * power >= value) {
      return step * power;
    }
  }
  return 10 * power;
}

/** Which days are named under the axis: all of a week, else 5 (3 on a phone), first and last kept. */
export function namedDays(count: number, width: number): readonly number[] {
  if (count <= 7) {
    return Array.from({ length: count }, (_, index) => index);
  }
  const labels = width < 480 ? 3 : 5;
  return Array.from({ length: labels }, (_, position) =>
    Math.round((position * (count - 1)) / (labels - 1)),
  );
}

/* ------------------------------------------------------------------------------- the table */

function DayTable({
  title,
  days,
  before,
  published,
  these,
  beforeWords,
}: {
  readonly title: string;
  readonly days: readonly ChartDay[];
  readonly before: readonly number[] | undefined;
  readonly published: ReadonlySet<string> | undefined;
  readonly these: string;
  readonly beforeWords: string;
}) {
  const { t } = useTranslation('articles');
  return (
    <table className={classes.table}>
      <caption className={classes.caption}>{t('stats.dayChart.tableCaption', { title })}</caption>
      <thead>
        <tr>
          <th scope="col">{t('stats.dayChart.day')}</th>
          <th scope="col" className={classes.number}>
            {these}
          </th>
          {before === undefined ? null : (
            <th scope="col" className={classes.number}>
              {beforeWords}
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {days.map((day, index) => (
          <tr key={day.day}>
            <th scope="row" className={classes.dayCell}>
              {formatShortDay(day.day)}
              {published?.has(day.day) === true ? (
                <span className={classes.readoutQuiet}> · {t('stats.dayChart.youPublished')}</span>
              ) : null}
            </th>
            <td className={classes.number}>{formatCount(day.value)}</td>
            {before === undefined ? null : (
              <td className={classes.number}>{formatCount(before[index] ?? 0)}</td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
